import type { ContestLeaderboardItem, ContestPrizeItem } from '../../../helpers/utils/types';
import { sanitizeRentDropTerminology } from '../../../helpers/utils/rentDropTerminology';

export type PrizeTierVariant = 'featured' | 'gold' | 'silver' | 'bronze' | 'bonus' | 'default';

export type MappedPrizeTier = {
  prize: ContestPrizeItem;
  label: string;
  variant: PrizeTierVariant;
  barWidthPct: number;
};

export const isTicketPrize = (prize: ContestPrizeItem) =>
  prize.prizeTypeName === 'ticket' || /ticket/i.test(prize.displayName);

export const isFeaturedPrize = (prize: ContestPrizeItem) =>
  prize.awardRank === 1 || /free\s*rent/i.test(prize.displayName);

export const formatPrizeLabel = (prize: ContestPrizeItem, featured = false): string => {
  if (featured) {
    if (/free\s*rent/i.test(prize.displayName)) return '1 month of free rent';
    return sanitizeRentDropTerminology(prize.displayName);
  }
  if (isTicketPrize(prize)) {
    const amt = Number(prize.amount);
    if (!Number.isNaN(amt) && amt > 0) {
      const n = Math.round(amt);
      return n === 1 ? '1 bonus ticket' : `${n} bonus tickets`;
    }
    return sanitizeRentDropTerminology(prize.displayName);
  }
  const amt = Number(prize.amount);
  if (!Number.isNaN(amt) && amt > 0) {
    const formatted = amt >= 1000 ? amt.toLocaleString('en-US') : String(amt);
    return `$${formatted} rent drop`;
  }
  return sanitizeRentDropTerminology(prize.displayName);
};

export const getPrizeTierVariant = (prize: ContestPrizeItem, featured = false): PrizeTierVariant => {
  if (featured) return 'featured';
  if (isTicketPrize(prize)) return 'bonus';
  const amt = Number(prize.amount);
  if (amt >= 100) return 'gold';
  if (amt >= 50) return 'silver';
  if (amt >= 20) return 'bronze';
  return 'default';
};

export const tierBarWidthPct = (quantity: number, poolTotal: number): number => {
  if (poolTotal <= 0) return 0;
  return Math.max(0.75, (quantity / poolTotal) * 100);
};

export const mapContestPrizes = (prizes: ContestPrizeItem[]): MappedPrizeTier[] => {
  const poolTotal = prizes.reduce((sum, p) => sum + p.quantity, 0);
  const featured = prizes.find(isFeaturedPrize);

  return prizes.map((prize) => {
    const featuredRow = featured?.prizeId === prize.prizeId;
    return {
      prize,
      label: formatPrizeLabel(prize, featuredRow),
      variant: getPrizeTierVariant(prize, featuredRow),
      barWidthPct: tierBarWidthPct(prize.quantity, poolTotal),
    };
  });
};

export const formatWinnerReward = (row: ContestLeaderboardItem): string => {
  const name = row.prizeName?.trim();
  if (name) {
    if (/free\s*rent/i.test(name)) return '1 month of free rent';
    return sanitizeRentDropTerminology(name);
  }
  const amt = Number(row.prizeAmount);
  if (!Number.isNaN(amt) && amt > 0) {
    const formatted = amt >= 1000 ? amt.toLocaleString('en-US') : String(amt);
    return `$${formatted} rent drop`;
  }
  if (row.prizeTypeName === 'ticket') {
    const ticketCount = Math.round(amt) || row.prizeQuantity;
    return ticketCount === 1 ? '1 bonus ticket' : `${ticketCount} bonus tickets`;
  }
  return row.prizeTypeName || 'Rent drop';
};

export type WinnerRewardTone = 'gold' | 'green' | 'blue';

export const getWinnerRewardTone = (reward: string): WinnerRewardTone => {
  if (/free rent|month of free rent/i.test(reward)) return 'gold';
  if (/ticket|pts|visa|nike|sephora/i.test(reward)) return 'green';
  return 'blue';
};

export const previousMonthDropLabel = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.toLocaleString('en-US', { month: 'long' })} ${d.getFullYear()} drop`;
};

export const toInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'TP';
  if (parts.length === 1) return (parts[0][0] ?? 'T').toUpperCase();
  return `${(parts[0][0] ?? '').toUpperCase()}${(parts[1][0] ?? '').toUpperCase()}`;
};
